import type * as React from 'react';

import { Celula } from '@/componentes/celula';
import { Button } from '@/componentes/ui/button';
import { Skeleton } from '@/componentes/ui/skeleton';

/* estado-da-tela.tsx — carregando, vazio e falha, com o TEXTO junto.
 *
 * Sete telas escreviam a própria versão do estado de falha, e elas já tinham
 * divergido: umas com ponto final, outras sem; umas com botão de tentar de
 * novo, outras sem; alturas de esqueleto escolhidas no olho, arquivo a arquivo.
 *
 * O que este módulo carrega, e é por isso que ele existe, é a REDAÇÃO, não só
 * a geometria. Um componente que padroniza a caixa e deixa cada tela escrever
 * a frase não resolve nada: em duas semanas há sete frases de novo.
 *
 * As regras de texto vêm do CLAUDE.md e valem aqui sem exceção. Erro diz o que
 * aconteceu e o que fazer, nunca pede desculpa. Vazio diz qual ETAPA preenche
 * o que falta, porque "sem dados" manda a pessoa procurar sozinha.
 */

/**
 * O esqueleto repete o token do que vai nascer no lugar dele.
 *
 * `linhas` é quantos blocos de conteúdo a tela terá, não uma altura em pixels:
 * altura em pixels é o que produziu dez medidas diferentes, cada uma escolhida
 * no olho de quem escreveu aquela tela.
 */
export function Carregando({ linhas = 3, rotulo }: { linhas?: number; rotulo?: string }) {
  return (
    <div className="favo" role="status" aria-busy="true">
      <span className="sr-only">{rotulo ?? 'Carregando.'}</span>
      {Array.from({ length: linhas }, (_, i) => (
        <Skeleton key={i} className="h-32 w-full rounded-xl" />
      ))}
    </div>
  );
}

/**
 * Falha de carregamento.
 *
 * `oQue` completa a frase "Não foi possível carregar ___", em minúscula e sem
 * ponto final, porque quem põe a pontuação é o componente. Foi assim que as
 * sete versões divergiram.
 */
export function Falha({
  oQue,
  motivo,
  aoTentarDeNovo,
}: {
  oQue: string;
  motivo?: string;
  aoTentarDeNovo?: () => void;
}) {
  return (
    <Celula campo="telha" respiro="folgado" role="alert" className="grid justify-items-start gap-3">
      <p className="titulo-bloco">Não foi possível carregar {oQue}.</p>
      {motivo === undefined ? null : <p className="apoio">{motivo}</p>}
      {aoTentarDeNovo === undefined ? null : (
        <Button variant="outline" onClick={aoTentarDeNovo}>
          Tentar de novo
        </Button>
      )}
    </Celula>
  );
}

/**
 * Vazio: existe a tela, não existe o dado ainda.
 *
 * `ondePreencher` é obrigatório de propósito. Um vazio que não diz qual etapa
 * resolve manda a pessoa procurar, e o dossiê registrou isso como o defeito de
 * redação mais repetido do produto.
 */
export function Vazio({
  titulo,
  ondePreencher,
  acao,
}: {
  titulo: string;
  ondePreencher: string;
  acao?: React.ReactNode;
}) {
  return (
    <Celula campo="areia" respiro="folgado" className="grid justify-items-start gap-3">
      <p className="titulo-bloco">{titulo}</p>
      <p className="apoio">{ondePreencher}</p>
      {acao}
    </Celula>
  );
}
